import './p-BJEvNVLM.js';

const defaultOptions = {
    elementDelimiter: "__",
    modifierDelimiter: "--",
    namespace: "",
    namespaceDelimiter: "-",
    strict: true,
};
/**
 * Set up the default options.
 *
 * @param options - Options to control a generated class name.
 */
function setup({ elementDelimiter, modifierDelimiter, namespace, namespaceDelimiter, strict, }) {
    if (typeof elementDelimiter === "string") {
        defaultOptions.elementDelimiter = elementDelimiter;
    }
    if (typeof modifierDelimiter === "string") {
        defaultOptions.modifierDelimiter = modifierDelimiter;
    }
    if (typeof namespace === "string" || Array.isArray(namespace)) {
        defaultOptions.namespace = namespace;
    }
    if (typeof namespaceDelimiter === "string") {
        defaultOptions.namespaceDelimiter = namespaceDelimiter;
    }
    {
        defaultOptions.strict = strict;
    }
}
const uniqueChars = (list) => list
    .join("")
    .split("")
    .filter((value, index, self) => self.indexOf(value) === index);
const includesChars = (str, chars) => chars.some((char) => str.includes(char));
const invalidMessage = (subject, subjectValue, delimiters) => {
    const delims = `"${delimiters.join('", "')}"`;
    return `The ${subject} ("${subjectValue}") must not use the characters contained within the delimiters (${delims}).`;
};
/**
 * Creates a function to generate a BEM class name.
 *
 * @param block - A BEM block name.
 * @param options - Options to control a generated class name.
 * @returns A function to generate a BEM class name.
 */
function bem$1(block, options = {}) {
    const { elementDelimiter, modifierDelimiter, namespace, namespaceDelimiter, strict } = {
        ...defaultOptions,
        ...options,
    };
    const namespaces = []
        .concat(namespace)
        .filter(Boolean) // compact
        .reduce((joined, ns) => `${joined}${ns}${namespaceDelimiter}`, "");
    const namespaceBlock = `${namespaces}${block}`;
    const delimiters = strict ? [namespaceDelimiter, elementDelimiter, modifierDelimiter] : [];
    const delimiterChars = strict ? uniqueChars(delimiters) : [];
    return function bemBlock(elementOrModifiers, modifiers) {
        if (elementOrModifiers == null) {
            return namespaceBlock;
        }
        const element = typeof elementOrModifiers === "string" ? elementOrModifiers : null;
        if (strict && element != null && includesChars(element, delimiterChars)) {
            throw new Error(invalidMessage("element", element, delimiters));
        }
        const base = element == null ? namespaceBlock : `${namespaceBlock}${elementDelimiter}${element}`;
        const mods = typeof elementOrModifiers === "string" ? modifiers : elementOrModifiers;
        if (mods == null) {
            return base;
        }
        const addModifiers = (className, modifier) => {
            if (modifier != null && modifier !== "") {
                if (strict && includesChars(modifier, delimiterChars)) {
                    throw new Error(invalidMessage("modifier", modifier, delimiters));
                }
                return `${className} ${base}${modifierDelimiter}${modifier}`;
            }
            return className;
        };
        if (Array.isArray(mods)) {
            return mods.reduce(addModifiers, base);
        }
        return Object.keys(mods)
            .filter((mod) => mods[mod])
            .reduce(addModifiers, base);
    };
}

setup({
    strict: false,
});
const transformModifierToArray = (modifiers) => {
    if (typeof modifiers !== 'object') {
        return modifiers;
    }
    return Object.keys(modifiers).reduce((acc, key) => {
        const value = modifiers[key];
        if (!value) {
            return acc;
        }
        if (value === true) {
            return [...acc, key];
        }
        /* if an object is passed we assume that we want to create responsive classes
         * e.g. the current key being "layout" and this passed as value:
         * { small: "compact", medium: "normal" }
         * we want to create the classes $BLOCK--layout-compact@small and $BLOCK--layout-normal@medium
         */
        if (typeof value === 'object') {
            const generatedResponsiveClasses = Object.keys(value).map((breakpoint) => {
                return `${key}-${value[breakpoint]}@${breakpoint}`;
            });
            return [...acc, ...generatedResponsiveClasses];
        }
        return [...acc, `${key}-${value}`];
    }, []);
};
const bem = (block) => {
    return (elementOrModifiers, modifiers) => {
        const element = typeof elementOrModifiers === 'string' ? elementOrModifiers : transformModifierToArray(elementOrModifiers);
        return bem$1(block)(element, transformModifierToArray(modifiers));
    };
};

// eslint-disable-next-line no-restricted-globals
const global$1 = typeof window === 'undefined' ? {} : window;

// FIXME: temporary solution to support both libraries without context
// alternative solutions: rename to more generic "dsConfig" or make it context aware
let disableWarnings = !!global$1.cosmosConfig?.disableWarnings || !!global$1.gravityConfig?.disableWarnings;
/**
 * Global config
 * @typedef {Object} Config
 * @property {boolean} disableWarnings - Flag to disable console warnings
 */
const config = {
    get disableWarnings() {
        return disableWarnings;
    },
    set disableWarnings(value) {
        disableWarnings = value;
    },
};
// Makes the config available
global$1.cosmosConfig = config;
global$1.gravityConfig = config;

/**
 * Wait for next tick
 * It is necessary to call requestAnimationFrame twice to ensure we are at the next repaint
 * https://developer.mozilla.org/en-US/docs/Web/API/window/requestAnimationFrame
 *
 * @return Promise<void>
 */
const nextTick = () => new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => {
        resolve();
    }));
});

/**
 * CSSTransition Class
 *
 * Implement transition classes to an element
 * Based on Angular and React CSS transitions:
 * http://reactcommunity.org/react-transition-group/css-transition
 *
 * @method enter(node: Element, duration: number): Promise<void>
 * @method leave(node: Element, duration: number): Promise<void>
 */
class CSSTransition {
    constructor() {
        this.classNames = {
            enter: 'enter',
            leave: 'leave',
        };
        this.timeouts = {
            enter: undefined,
            leave: undefined,
        };
    }
    async enter(node, duration) {
        if (!node) {
            return Promise.reject();
        }
        const classNames = this.getClassNames();
        this.clearTimeouts();
        this.clearClassNames(node);
        this.addClassName(node, classNames['enter']);
        await this.addClassNameOnNextTick(node, classNames['enterActive']);
        return new Promise((resolve) => {
            this.timeouts.enter = setTimeout(() => {
                this.clearClassNames(node);
                resolve();
            }, duration);
        });
    }
    async leave(node, duration) {
        if (!node) {
            return Promise.reject();
        }
        const classNames = this.getClassNames();
        this.clearTimeouts();
        this.clearClassNames(node);
        this.addClassName(node, classNames['leave']);
        await this.addClassNameOnNextTick(node, classNames['leaveActive']);
        return new Promise((resolve) => {
            this.timeouts.leave = setTimeout(() => {
                this.clearClassNames(node);
                resolve();
            }, duration);
        });
    }
    getClassNames() {
        return {
            enter: this.classNames.enter,
            enterActive: `${this.classNames.enter}-active`,
            leave: this.classNames.leave,
            leaveActive: `${this.classNames.leave}-active`,
        };
    }
    addClassName(node, className) {
        node.classList.add(className);
    }
    clearTimeouts() {
        Object.values(this.timeouts).forEach((timeout) => {
            if (timeout) {
                clearTimeout(timeout);
            }
        });
    }
    clearClassNames(node) {
        const classNames = this.getClassNames();
        Object.values(classNames).forEach((className) => {
            this.removeClassName(node, className);
        });
    }
    removeClassName(node, className) {
        node.classList.remove(className);
    }
    async addClassNameOnNextTick(node, className) {
        // Waiting for next repaint to ensure the class is added in next frame
        await nextTick();
        this.addClassName(node, className);
    }
}

var commonjsGlobal = typeof globalThis !== 'undefined' ? globalThis : typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : typeof self !== 'undefined' ? self : {};

/** `Object#toString` result references. */
var symbolTag$1 = '[object Symbol]';

/** Used to match words composed of alphanumeric characters. */
var reAsciiWord$1 = /[^\x00-\x2f\x3a-\x40\x5b-\x60\x7b-\x7f]+/g;

/** Used to match Latin Unicode letters (excluding mathematical operators). */
var reLatin$1 = /[\xc0-\xd6\xd8-\xf6\xf8-\xff\u0100-\u017f]/g;

/** Used to compose unicode character classes. */
var rsAstralRange$1 = '\\ud800-\\udfff',
    rsComboMarksRange$1 = '\\u0300-\\u036f\\ufe20-\\ufe23',
    rsComboSymbolsRange$1 = '\\u20d0-\\u20f0',
    rsDingbatRange$1 = '\\u2700-\\u27bf',
    rsLowerRange$1 = 'a-z\\xdf-\\xf6\\xf8-\\xff',
    rsMathOpRange$1 = '\\xac\\xb1\\xd7\\xf7',
    rsNonCharRange$1 = '\\x00-\\x2f\\x3a-\\x40\\x5b-\\x60\\x7b-\\xbf',
    rsPunctuationRange$1 = '\\u2000-\\u206f',
    rsSpaceRange$1 = ' \\t\\x0b\\f\\xa0\\ufeff\\n\\r\\u2028\\u2029\\u1680\\u180e\\u2000\\u2001\\u2002\\u2003\\u2004\\u2005\\u2006\\u2007\\u2008\\u2009\\u200a\\u202f\\u205f\\u3000',
    rsUpperRange$1 = 'A-Z\\xc0-\\xd6\\xd8-\\xde',
    rsVarRange$1 = '\\ufe0e\\ufe0f',
    rsBreakRange$1 = rsMathOpRange$1 + rsNonCharRange$1 + rsPunctuationRange$1 + rsSpaceRange$1;

/** Used to compose unicode capture groups. */
var rsApos$1 = "['\u2019]",
    rsBreak$1 = '[' + rsBreakRange$1 + ']',
    rsCombo$1 = '[' + rsComboMarksRange$1 + rsComboSymbolsRange$1 + ']',
    rsDigits$1 = '\\d+',
    rsDingbat$1 = '[' + rsDingbatRange$1 + ']',
    rsLower$1 = '[' + rsLowerRange$1 + ']',
    rsMisc$1 = '[^' + rsAstralRange$1 + rsBreakRange$1 + rsDigits$1 + rsDingbatRange$1 + rsLowerRange$1 + rsUpperRange$1 + ']',
    rsFitz$1 = '\\ud83c[\\udffb-\\udfff]',
    rsModifier$1 = '(?:' + rsCombo$1 + '|' + rsFitz$1 + ')',
    rsNonAstral$1 = '[^' + rsAstralRange$1 + ']',
    rsRegional$1 = '(?:\\ud83c[\\udde6-\\uddff]){2}',
    rsSurrPair$1 = '[\\ud800-\\udbff][\\udc00-\\udfff]',
    rsUpper$1 = '[' + rsUpperRange$1 + ']',
    rsZWJ$1 = '\\u200d';

/** Used to compose unicode regexes. */
var rsLowerMisc$1 = '(?:' + rsLower$1 + '|' + rsMisc$1 + ')',
    rsUpperMisc$1 = '(?:' + rsUpper$1 + '|' + rsMisc$1 + ')',
    rsOptLowerContr$1 = '(?:' + rsApos$1 + '(?:d|ll|m|re|s|t|ve))?',
    rsOptUpperContr$1 = '(?:' + rsApos$1 + '(?:D|LL|M|RE|S|T|VE))?',
    reOptMod$1 = rsModifier$1 + '?',
    rsOptVar$1 = '[' + rsVarRange$1 + ']?',
    rsOptJoin$1 = '(?:' + rsZWJ$1 + '(?:' + [rsNonAstral$1, rsRegional$1, rsSurrPair$1].join('|') + ')' + rsOptVar$1 + reOptMod$1 + ')*',
    rsSeq$1 = rsOptVar$1 + reOptMod$1 + rsOptJoin$1,
    rsEmoji$1 = '(?:' + [rsDingbat$1, rsRegional$1, rsSurrPair$1].join('|') + ')' + rsSeq$1;

/** Used to match apostrophes. */
var reApos$1 = RegExp(rsApos$1, 'g');

/**
 * Used to match [combining diacritical marks](https://en.wikipedia.org/wiki/Combining_Diacritical_Marks) and
 * [combining diacritical marks for symbols](https://en.wikipedia.org/wiki/Combining_Diacritical_Marks_for_Symbols).
 */
var reComboMark$1 = RegExp(rsCombo$1, 'g');

/** Used to match complex or compound words. */
var reUnicodeWord$1 = RegExp([
  rsUpper$1 + '?' + rsLower$1 + '+' + rsOptLowerContr$1 + '(?=' + [rsBreak$1, rsUpper$1, '$'].join('|') + ')',
  rsUpperMisc$1 + '+' + rsOptUpperContr$1 + '(?=' + [rsBreak$1, rsUpper$1 + rsLowerMisc$1, '$'].join('|') + ')',
  rsUpper$1 + '?' + rsLowerMisc$1 + '+' + rsOptLowerContr$1,
  rsUpper$1 + '+' + rsOptUpperContr$1,
  rsDigits$1,
  rsEmoji$1
].join('|'), 'g');

/** Used to detect strings that need a more robust regexp to match words. */
var reHasUnicodeWord$1 = /[a-z][A-Z]|[A-Z]{2,}[a-z]|[0-9][a-zA-Z]|[a-zA-Z][0-9]|[^a-zA-Z0-9 ]/;

/** Used to map Latin Unicode letters to basic Latin letters. */
var deburredLetters$1 = {
  // Latin-1 Supplement block.
  '\xc0': 'A',  '\xc1': 'A', '\xc2': 'A', '\xc3': 'A', '\xc4': 'A', '\xc5': 'A',
  '\xe0': 'a',  '\xe1': 'a', '\xe2': 'a', '\xe3': 'a', '\xe4': 'a', '\xe5': 'a',
  '\xc7': 'C',  '\xe7': 'c',
  '\xd0': 'D',  '\xf0': 'd',
  '\xc8': 'E',  '\xc9': 'E', '\xca': 'E', '\xcb': 'E',
  '\xe8': 'e',  '\xe9': 'e', '\xea': 'e', '\xeb': 'e',
  '\xcc': 'I',  '\xcd': 'I', '\xce': 'I', '\xcf': 'I',
  '\xec': 'i',  '\xed': 'i', '\xee': 'i', '\xef': 'i',
  '\xd1': 'N',  '\xf1': 'n',
  '\xd2': 'O',  '\xd3': 'O', '\xd4': 'O', '\xd5': 'O', '\xd6': 'O', '\xd8': 'O',
  '\xf2': 'o',  '\xf3': 'o', '\xf4': 'o', '\xf5': 'o', '\xf6': 'o', '\xf8': 'o',
  '\xd9': 'U',  '\xda': 'U', '\xdb': 'U', '\xdc': 'U',
  '\xf9': 'u',  '\xfa': 'u', '\xfb': 'u', '\xfc': 'u',
  '\xdd': 'Y',  '\xfd': 'y', '\xff': 'y',
  '\xc6': 'Ae', '\xe6': 'ae',
  '\xde': 'Th', '\xfe': 'th',
  '\xdf': 'ss',
  // Latin Extended-A block.
  '\u0100': 'A',  '\u0102': 'A', '\u0104': 'A',
  '\u0101': 'a',  '\u0103': 'a', '\u0105': 'a',
  '\u0106': 'C',  '\u0108': 'C', '\u010a': 'C', '\u010c': 'C',
  '\u0107': 'c',  '\u0109': 'c', '\u010b': 'c', '\u010d': 'c',
  '\u010e': 'D',  '\u0110': 'D', '\u010f': 'd', '\u0111': 'd',
  '\u0112': 'E',  '\u0114': 'E', '\u0116': 'E', '\u0118': 'E', '\u011a': 'E',
  '\u0113': 'e',  '\u0115': 'e', '\u0117': 'e', '\u0119': 'e', '\u011b': 'e',
  '\u011c': 'G',  '\u011e': 'G', '\u0120': 'G', '\u0122': 'G',
  '\u011d': 'g',  '\u011f': 'g', '\u0121': 'g', '\u0123': 'g',
  '\u0124': 'H',  '\u0126': 'H', '\u0125': 'h', '\u0127': 'h',
  '\u0128': 'I',  '\u012a': 'I', '\u012c': 'I', '\u012e': 'I', '\u0130': 'I',
  '\u0129': 'i',  '\u012b': 'i', '\u012d': 'i', '\u012f': 'i', '\u0131': 'i',
  '\u0134': 'J',  '\u0135': 'j',
  '\u0136': 'K',  '\u0137': 'k', '\u0138': 'k',
  '\u0139': 'L',  '\u013b': 'L', '\u013d': 'L', '\u013f': 'L', '\u0141': 'L',
  '\u013a': 'l',  '\u013c': 'l', '\u013e': 'l', '\u0140': 'l', '\u0142': 'l',
  '\u0143': 'N',  '\u0145': 'N', '\u0147': 'N', '\u014a': 'N',
  '\u0144': 'n',  '\u0146': 'n', '\u0148': 'n', '\u014b': 'n',
  '\u014c': 'O',  '\u014e': 'O', '\u0150': 'O',
  '\u014d': 'o',  '\u014f': 'o', '\u0151': 'o',
  '\u0154': 'R',  '\u0156': 'R', '\u0158': 'R',
  '\u0155': 'r',  '\u0157': 'r', '\u0159': 'r',
  '\u015a': 'S',  '\u015c': 'S', '\u015e': 'S', '\u0160': 'S',
  '\u015b': 's',  '\u015d': 's', '\u015f': 's', '\u0161': 's',
  '\u0162': 'T',  '\u0164': 'T', '\u0166': 'T',
  '\u0163': 't',  '\u0165': 't', '\u0167': 't',
  '\u0168': 'U',  '\u016a': 'U', '\u016c': 'U', '\u016e': 'U', '\u0170': 'U', '\u0172': 'U',
  '\u0169': 'u',  '\u016b': 'u', '\u016d': 'u', '\u016f': 'u', '\u0171': 'u', '\u0173': 'u',
  '\u0174': 'W',  '\u0175': 'w',
  '\u0176': 'Y',  '\u0177': 'y', '\u0178': 'Y',
  '\u0179': 'Z',  '\u017b': 'Z', '\u017d': 'Z',
  '\u017a': 'z',  '\u017c': 'z', '\u017e': 'z',
  '\u0132': 'IJ', '\u0133': 'ij',
  '\u0152': 'Oe', '\u0153': 'oe',
  '\u0149': "'n", '\u017f': 'ss'
};

/** Detect free variable `global` from Node.js. */
var freeGlobal$1 = typeof commonjsGlobal == 'object' && commonjsGlobal && commonjsGlobal.Object === Object && commonjsGlobal;

/** Detect free variable `self`. */
var freeSelf$1 = typeof self == 'object' && self && self.Object === Object && self;

/** Used as a reference to the global object. */
var root$1 = freeGlobal$1 || freeSelf$1 || Function('return this')();

/**
 * A specialized version of `_.reduce` for arrays without support for
 * iteratee shorthands.
 *
 * @private
 * @param {Array} [array] The array to iterate over.
 * @param {Function} iteratee The function invoked per iteration.
 * @param {*} [accumulator] The initial value.
 * @param {boolean} [initAccum] Specify using the first element of `array` as
 *  the initial value.
 * @returns {*} Returns the accumulated value.
 */
function arrayReduce$1(array, iteratee, accumulator, initAccum) {
  var index = -1,
      length = array ? array.length : 0;
  while (++index < length) {
    accumulator = iteratee(accumulator, array[index], index, array);
  }
  return accumulator;
}

/**
 * Splits an ASCII `string` into an array of its words.
 *
 * @private
 * @param {string} The string to inspect.
 * @returns {Array} Returns the words of `string`.
 */
function asciiWords$1(string) {
  return string.match(reAsciiWord$1) || [];
}

/**
 * The base implementation of `_.propertyOf` without support for deep paths.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Function} Returns the new accessor function.
 */
function basePropertyOf$1(object) {
  return function(key) {
    return object == null ? undefined : object[key];
  };
}

/**
 * Used by `_.deburr` to convert Latin-1 Supplement and Latin Extended-A
 * letters to basic Latin letters.
 *
 * @private
 * @param {string} letter The matched letter to deburr.
 * @returns {string} Returns the deburred letter.
 */
var deburrLetter$1 = basePropertyOf$1(deburredLetters$1);

/**
 * Checks if `string` contains a word composed of Unicode symbols.
 *
 * @private
 * @param {string} string The string to inspect.
 * @returns {boolean} Returns `true` if a word is found, else `false`.
 */
function hasUnicodeWord$1(string) {
  return reHasUnicodeWord$1.test(string);
}

/**
 * Splits a Unicode `string` into an array of its words.
 *
 * @private
 * @param {string} The string to inspect.
 * @returns {Array} Returns the words of `string`.
 */
function unicodeWords$1(string) {
  return string.match(reUnicodeWord$1) || [];
}

/** Used for built-in method references. */
var objectProto$1 = Object.prototype;

/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */
var objectToString$1 = objectProto$1.toString;

/** Built-in value references. */
var Symbol$1 = root$1.Symbol;

/** Used to convert symbols to primitives and strings. */
var symbolProto$1 = Symbol$1 ? Symbol$1.prototype : undefined,
    symbolToString$1 = symbolProto$1 ? symbolProto$1.toString : undefined;

/**
 * The base implementation of `_.toString` which doesn't convert nullish
 * values to empty strings.
 *
 * @private
 * @param {*} value The value to process.
 * @returns {string} Returns the string.
 */
function baseToString$1(value) {
  // Exit early for strings to avoid a performance hit in some environments.
  if (typeof value == 'string') {
    return value;
  }
  if (isSymbol$1(value)) {
    return symbolToString$1 ? symbolToString$1.call(value) : '';
  }
  var result = (value + '');
  return (result == '0' && (1 / value) == -Infinity) ? '-0' : result;
}

/**
 * Creates a function like `_.camelCase`.
 *
 * @private
 * @param {Function} callback The function to combine each word.
 * @returns {Function} Returns the new compounder function.
 */
function createCompounder$1(callback) {
  return function(string) {
    return arrayReduce$1(words$1(deburr$1(string).replace(reApos$1, '')), callback, '');
  };
}

/**
 * Checks if `value` is object-like. A value is object-like if it's not `null`
 * and has a `typeof` result of "object".
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is object-like, else `false`.
 * @example
 *
 * _.isObjectLike({});
 * // => true
 *
 * _.isObjectLike([1, 2, 3]);
 * // => true
 *
 * _.isObjectLike(_.noop);
 * // => false
 *
 * _.isObjectLike(null);
 * // => false
 */
function isObjectLike$1(value) {
  return !!value && typeof value == 'object';
}

/**
 * Checks if `value` is classified as a `Symbol` primitive or object.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a symbol, else `false`.
 * @example
 *
 * _.isSymbol(Symbol.iterator);
 * // => true
 *
 * _.isSymbol('abc');
 * // => false
 */
function isSymbol$1(value) {
  return typeof value == 'symbol' ||
    (isObjectLike$1(value) && objectToString$1.call(value) == symbolTag$1);
}

/**
 * Converts `value` to a string. An empty string is returned for `null`
 * and `undefined` values. The sign of `-0` is preserved.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to process.
 * @returns {string} Returns the string.
 * @example
 *
 * _.toString(null);
 * // => ''
 *
 * _.toString(-0);
 * // => '-0'
 *
 * _.toString([1, 2, 3]);
 * // => '1,2,3'
 */
function toString$1(value) {
  return value == null ? '' : baseToString$1(value);
}

/**
 * Deburrs `string` by converting
 * [Latin-1 Supplement](https://en.wikipedia.org/wiki/Latin-1_Supplement_(Unicode_block)#Character_table)
 * and [Latin Extended-A](https://en.wikipedia.org/wiki/Latin_Extended-A)
 * letters to basic Latin letters and removing
 * [combining diacritical marks](https://en.wikipedia.org/wiki/Combining_Diacritical_Marks).
 *
 * @static
 * @memberOf _
 * @since 3.0.0
 * @category String
 * @param {string} [string=''] The string to deburr.
 * @returns {string} Returns the deburred string.
 * @example
 *
 * _.deburr('déjà vu');
 * // => 'deja vu'
 */
function deburr$1(string) {
  string = toString$1(string);
  return string && string.replace(reLatin$1, deburrLetter$1).replace(reComboMark$1, '');
}

/**
 * Converts `string` to
 * [kebab case](https://en.wikipedia.org/wiki/Letter_case#Special_case_styles).
 *
 * @static
 * @memberOf _
 * @since 3.0.0
 * @category String
 * @param {string} [string=''] The string to convert.
 * @returns {string} Returns the kebab cased string.
 * @example
 *
 * _.kebabCase('Foo Bar');
 * // => 'foo-bar'
 *
 * _.kebabCase('fooBar');
 * // => 'foo-bar'
 *
 * _.kebabCase('__FOO_BAR__');
 * // => 'foo-bar'
 */
var kebabCase = createCompounder$1(function(result, word, index) {
  return result + (index ? '-' : '') + word.toLowerCase();
});

/**
 * Splits `string` into an array of its words.
 *
 * @static
 * @memberOf _
 * @since 3.0.0
 * @category String
 * @param {string} [string=''] The string to inspect.
 * @param {RegExp|string} [pattern] The pattern to match words.
 * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
 * @returns {Array} Returns the words of `string`.
 * @example
 *
 * _.words('fred, barney, & pebbles');
 * // => ['fred', 'barney', 'pebbles']
 *
 * _.words('fred, barney, & pebbles', /[^, ]+/g);
 * // => ['fred', 'barney', '&', 'pebbles']
 */
function words$1(string, pattern, guard) {
  string = toString$1(string);
  pattern = pattern;

  if (pattern === undefined) {
    return hasUnicodeWord$1(string) ? unicodeWords$1(string) : asciiWords$1(string);
  }
  return string.match(pattern) || [];
}

var lodash_kebabcase = kebabCase;

/**
 * Formats a member's name based on its deprecation type.
 * @param name - Member's name.
 * @param type - Deprecation type.
 * @returns Formatted member name.
 */
const formatMemberName = (name, type) => {
    switch (type) {
        case 'property':
            return lodash_kebabcase(name);
        case 'method':
            return `${name}()`;
        default:
            return name;
    }
};
/**
 * Retrieves global document's styleSheets, if in a browser context.
 * @returns StyleSheetList or undefined.
 */
const getStyleSheets = () => {
    return global$1?.document?.styleSheets;
};
/**
 * Filters out external style sheets to avoid CORS issues.
 * @param styleSheets - Style sheets to filter.
 * @returns Filtered CSSStyleSheets.
 */
const filterStyleSheets = (styleSheets) => {
    return Array.from(styleSheets).filter((styleSheet) => !styleSheet.href || styleSheet.href.startsWith(location.origin));
};
/**
 * Identifies component parts applied from given stylesheets.
 * @param component - Target component.
 * @param styleSheets - StyleSheets to check against.
 * @returns Applied component parts.
 */
const getAppliedComponentParts = (component, styleSheets) => {
    const parts = [];
    if (!styleSheets?.length) {
        return parts;
    }
    for (const sheet of styleSheets) {
        if (!('cssRules' in sheet)) {
            continue;
        }
        for (const rule of Object.values(sheet.cssRules)) {
            if (!('selectorText' in rule) || !rule.selectorText.includes('::part')) {
                continue;
            }
            rule.selectorText.split(',').forEach((selectorText) => {
                selectorText = selectorText.trim();
                if (!selectorText.includes('::part')) {
                    return;
                }
                const [selector, part] = selectorText.split('::part');
                if (component.matches(selector)) {
                    parts.push(part
                        // Remove parentheses from the part selector
                        .replace(/[{()}]/g, ''));
                }
            });
        }
    }
    // Remove duplicates and return sorted array
    return [...new Set(parts)].sort();
};
/**
 * Checks if a named or default slot is in use within a component.
 * @param component - Target component.
 * @param slotName - Slot name (checks for default slot if not provided).
 * @returns True if slot is in use, false otherwise.
 */
const isNamedSlotUsed = (component, slotName) => {
    if (slotName) {
        return !!component.querySelector(`[slot="${slotName}"]`);
    }
    return false;
};

/**
 * Handle console logs and warnings to the browser
 */
/**
 * Display a message as a warning in the browser console.
 *
 * @param {string} message The message to display as a warning.
 * @returns {void}
 * */
const warn = (message) => {
    if (config.disableWarnings) {
        return;
    }
    global$1.console.warn(`%c🪐 Gravity / Cosmos`, 'color: #000F1E; background: #FF900099; font-weight: bold; font-size: 10px; padding: 2px 6px; border-radius: 5px;', message);
};
const Logger = { warn };

/** `Object#toString` result references. */
var symbolTag = '[object Symbol]';

/** Used to match words composed of alphanumeric characters. */
var reAsciiWord = /[^\x00-\x2f\x3a-\x40\x5b-\x60\x7b-\x7f]+/g;

/** Used to match Latin Unicode letters (excluding mathematical operators). */
var reLatin = /[\xc0-\xd6\xd8-\xf6\xf8-\xff\u0100-\u017f]/g;

/** Used to compose unicode character classes. */
var rsAstralRange = '\\ud800-\\udfff',
    rsComboMarksRange = '\\u0300-\\u036f\\ufe20-\\ufe23',
    rsComboSymbolsRange = '\\u20d0-\\u20f0',
    rsDingbatRange = '\\u2700-\\u27bf',
    rsLowerRange = 'a-z\\xdf-\\xf6\\xf8-\\xff',
    rsMathOpRange = '\\xac\\xb1\\xd7\\xf7',
    rsNonCharRange = '\\x00-\\x2f\\x3a-\\x40\\x5b-\\x60\\x7b-\\xbf',
    rsPunctuationRange = '\\u2000-\\u206f',
    rsSpaceRange = ' \\t\\x0b\\f\\xa0\\ufeff\\n\\r\\u2028\\u2029\\u1680\\u180e\\u2000\\u2001\\u2002\\u2003\\u2004\\u2005\\u2006\\u2007\\u2008\\u2009\\u200a\\u202f\\u205f\\u3000',
    rsUpperRange = 'A-Z\\xc0-\\xd6\\xd8-\\xde',
    rsVarRange = '\\ufe0e\\ufe0f',
    rsBreakRange = rsMathOpRange + rsNonCharRange + rsPunctuationRange + rsSpaceRange;

/** Used to compose unicode capture groups. */
var rsApos = "['\u2019]",
    rsAstral = '[' + rsAstralRange + ']',
    rsBreak = '[' + rsBreakRange + ']',
    rsCombo = '[' + rsComboMarksRange + rsComboSymbolsRange + ']',
    rsDigits = '\\d+',
    rsDingbat = '[' + rsDingbatRange + ']',
    rsLower = '[' + rsLowerRange + ']',
    rsMisc = '[^' + rsAstralRange + rsBreakRange + rsDigits + rsDingbatRange + rsLowerRange + rsUpperRange + ']',
    rsFitz = '\\ud83c[\\udffb-\\udfff]',
    rsModifier = '(?:' + rsCombo + '|' + rsFitz + ')',
    rsNonAstral = '[^' + rsAstralRange + ']',
    rsRegional = '(?:\\ud83c[\\udde6-\\uddff]){2}',
    rsSurrPair = '[\\ud800-\\udbff][\\udc00-\\udfff]',
    rsUpper = '[' + rsUpperRange + ']',
    rsZWJ = '\\u200d';

/** Used to compose unicode regexes. */
var rsLowerMisc = '(?:' + rsLower + '|' + rsMisc + ')',
    rsUpperMisc = '(?:' + rsUpper + '|' + rsMisc + ')',
    rsOptLowerContr = '(?:' + rsApos + '(?:d|ll|m|re|s|t|ve))?',
    rsOptUpperContr = '(?:' + rsApos + '(?:D|LL|M|RE|S|T|VE))?',
    reOptMod = rsModifier + '?',
    rsOptVar = '[' + rsVarRange + ']?',
    rsOptJoin = '(?:' + rsZWJ + '(?:' + [rsNonAstral, rsRegional, rsSurrPair].join('|') + ')' + rsOptVar + reOptMod + ')*',
    rsSeq = rsOptVar + reOptMod + rsOptJoin,
    rsEmoji = '(?:' + [rsDingbat, rsRegional, rsSurrPair].join('|') + ')' + rsSeq,
    rsSymbol = '(?:' + [rsNonAstral + rsCombo + '?', rsCombo, rsRegional, rsSurrPair, rsAstral].join('|') + ')';

/** Used to match apostrophes. */
var reApos = RegExp(rsApos, 'g');

/**
 * Used to match [combining diacritical marks](https://en.wikipedia.org/wiki/Combining_Diacritical_Marks) and
 * [combining diacritical marks for symbols](https://en.wikipedia.org/wiki/Combining_Diacritical_Marks_for_Symbols).
 */
var reComboMark = RegExp(rsCombo, 'g');

/** Used to match [string symbols](https://mathiasbynens.be/notes/javascript-unicode). */
var reUnicode = RegExp(rsFitz + '(?=' + rsFitz + ')|' + rsSymbol + rsSeq, 'g');

/** Used to match complex or compound words. */
var reUnicodeWord = RegExp([
  rsUpper + '?' + rsLower + '+' + rsOptLowerContr + '(?=' + [rsBreak, rsUpper, '$'].join('|') + ')',
  rsUpperMisc + '+' + rsOptUpperContr + '(?=' + [rsBreak, rsUpper + rsLowerMisc, '$'].join('|') + ')',
  rsUpper + '?' + rsLowerMisc + '+' + rsOptLowerContr,
  rsUpper + '+' + rsOptUpperContr,
  rsDigits,
  rsEmoji
].join('|'), 'g');

/** Used to detect strings with [zero-width joiners or code points from the astral planes](http://eev.ee/blog/2015/09/12/dark-corners-of-unicode/). */
var reHasUnicode = RegExp('[' + rsZWJ + rsAstralRange  + rsComboMarksRange + rsComboSymbolsRange + rsVarRange + ']');

/** Used to detect strings that need a more robust regexp to match words. */
var reHasUnicodeWord = /[a-z][A-Z]|[A-Z]{2,}[a-z]|[0-9][a-zA-Z]|[a-zA-Z][0-9]|[^a-zA-Z0-9 ]/;

/** Used to map Latin Unicode letters to basic Latin letters. */
var deburredLetters = {
  // Latin-1 Supplement block.
  '\xc0': 'A',  '\xc1': 'A', '\xc2': 'A', '\xc3': 'A', '\xc4': 'A', '\xc5': 'A',
  '\xe0': 'a',  '\xe1': 'a', '\xe2': 'a', '\xe3': 'a', '\xe4': 'a', '\xe5': 'a',
  '\xc7': 'C',  '\xe7': 'c',
  '\xd0': 'D',  '\xf0': 'd',
  '\xc8': 'E',  '\xc9': 'E', '\xca': 'E', '\xcb': 'E',
  '\xe8': 'e',  '\xe9': 'e', '\xea': 'e', '\xeb': 'e',
  '\xcc': 'I',  '\xcd': 'I', '\xce': 'I', '\xcf': 'I',
  '\xec': 'i',  '\xed': 'i', '\xee': 'i', '\xef': 'i',
  '\xd1': 'N',  '\xf1': 'n',
  '\xd2': 'O',  '\xd3': 'O', '\xd4': 'O', '\xd5': 'O', '\xd6': 'O', '\xd8': 'O',
  '\xf2': 'o',  '\xf3': 'o', '\xf4': 'o', '\xf5': 'o', '\xf6': 'o', '\xf8': 'o',
  '\xd9': 'U',  '\xda': 'U', '\xdb': 'U', '\xdc': 'U',
  '\xf9': 'u',  '\xfa': 'u', '\xfb': 'u', '\xfc': 'u',
  '\xdd': 'Y',  '\xfd': 'y', '\xff': 'y',
  '\xc6': 'Ae', '\xe6': 'ae',
  '\xde': 'Th', '\xfe': 'th',
  '\xdf': 'ss',
  // Latin Extended-A block.
  '\u0100': 'A',  '\u0102': 'A', '\u0104': 'A',
  '\u0101': 'a',  '\u0103': 'a', '\u0105': 'a',
  '\u0106': 'C',  '\u0108': 'C', '\u010a': 'C', '\u010c': 'C',
  '\u0107': 'c',  '\u0109': 'c', '\u010b': 'c', '\u010d': 'c',
  '\u010e': 'D',  '\u0110': 'D', '\u010f': 'd', '\u0111': 'd',
  '\u0112': 'E',  '\u0114': 'E', '\u0116': 'E', '\u0118': 'E', '\u011a': 'E',
  '\u0113': 'e',  '\u0115': 'e', '\u0117': 'e', '\u0119': 'e', '\u011b': 'e',
  '\u011c': 'G',  '\u011e': 'G', '\u0120': 'G', '\u0122': 'G',
  '\u011d': 'g',  '\u011f': 'g', '\u0121': 'g', '\u0123': 'g',
  '\u0124': 'H',  '\u0126': 'H', '\u0125': 'h', '\u0127': 'h',
  '\u0128': 'I',  '\u012a': 'I', '\u012c': 'I', '\u012e': 'I', '\u0130': 'I',
  '\u0129': 'i',  '\u012b': 'i', '\u012d': 'i', '\u012f': 'i', '\u0131': 'i',
  '\u0134': 'J',  '\u0135': 'j',
  '\u0136': 'K',  '\u0137': 'k', '\u0138': 'k',
  '\u0139': 'L',  '\u013b': 'L', '\u013d': 'L', '\u013f': 'L', '\u0141': 'L',
  '\u013a': 'l',  '\u013c': 'l', '\u013e': 'l', '\u0140': 'l', '\u0142': 'l',
  '\u0143': 'N',  '\u0145': 'N', '\u0147': 'N', '\u014a': 'N',
  '\u0144': 'n',  '\u0146': 'n', '\u0148': 'n', '\u014b': 'n',
  '\u014c': 'O',  '\u014e': 'O', '\u0150': 'O',
  '\u014d': 'o',  '\u014f': 'o', '\u0151': 'o',
  '\u0154': 'R',  '\u0156': 'R', '\u0158': 'R',
  '\u0155': 'r',  '\u0157': 'r', '\u0159': 'r',
  '\u015a': 'S',  '\u015c': 'S', '\u015e': 'S', '\u0160': 'S',
  '\u015b': 's',  '\u015d': 's', '\u015f': 's', '\u0161': 's',
  '\u0162': 'T',  '\u0164': 'T', '\u0166': 'T',
  '\u0163': 't',  '\u0165': 't', '\u0167': 't',
  '\u0168': 'U',  '\u016a': 'U', '\u016c': 'U', '\u016e': 'U', '\u0170': 'U', '\u0172': 'U',
  '\u0169': 'u',  '\u016b': 'u', '\u016d': 'u', '\u016f': 'u', '\u0171': 'u', '\u0173': 'u',
  '\u0174': 'W',  '\u0175': 'w',
  '\u0176': 'Y',  '\u0177': 'y', '\u0178': 'Y',
  '\u0179': 'Z',  '\u017b': 'Z', '\u017d': 'Z',
  '\u017a': 'z',  '\u017c': 'z', '\u017e': 'z',
  '\u0132': 'IJ', '\u0133': 'ij',
  '\u0152': 'Oe', '\u0153': 'oe',
  '\u0149': "'n", '\u017f': 'ss'
};

/** Detect free variable `global` from Node.js. */
var freeGlobal = typeof commonjsGlobal == 'object' && commonjsGlobal && commonjsGlobal.Object === Object && commonjsGlobal;

/** Detect free variable `self`. */
var freeSelf = typeof self == 'object' && self && self.Object === Object && self;

/** Used as a reference to the global object. */
var root = freeGlobal || freeSelf || Function('return this')();

/**
 * A specialized version of `_.reduce` for arrays without support for
 * iteratee shorthands.
 *
 * @private
 * @param {Array} [array] The array to iterate over.
 * @param {Function} iteratee The function invoked per iteration.
 * @param {*} [accumulator] The initial value.
 * @param {boolean} [initAccum] Specify using the first element of `array` as
 *  the initial value.
 * @returns {*} Returns the accumulated value.
 */
function arrayReduce(array, iteratee, accumulator, initAccum) {
  var index = -1,
      length = array ? array.length : 0;
  while (++index < length) {
    accumulator = iteratee(accumulator, array[index], index, array);
  }
  return accumulator;
}

/**
 * Converts an ASCII `string` to an array.
 *
 * @private
 * @param {string} string The string to convert.
 * @returns {Array} Returns the converted array.
 */
function asciiToArray(string) {
  return string.split('');
}

/**
 * Splits an ASCII `string` into an array of its words.
 *
 * @private
 * @param {string} The string to inspect.
 * @returns {Array} Returns the words of `string`.
 */
function asciiWords(string) {
  return string.match(reAsciiWord) || [];
}

/**
 * The base implementation of `_.propertyOf` without support for deep paths.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Function} Returns the new accessor function.
 */
function basePropertyOf(object) {
  return function(key) {
    return object == null ? undefined : object[key];
  };
}

/**
 * Used by `_.deburr` to convert Latin-1 Supplement and Latin Extended-A
 * letters to basic Latin letters.
 *
 * @private
 * @param {string} letter The matched letter to deburr.
 * @returns {string} Returns the deburred letter.
 */
var deburrLetter = basePropertyOf(deburredLetters);

/**
 * Checks if `string` contains Unicode symbols.
 *
 * @private
 * @param {string} string The string to inspect.
 * @returns {boolean} Returns `true` if a symbol is found, else `false`.
 */
function hasUnicode(string) {
  return reHasUnicode.test(string);
}

/**
 * Checks if `string` contains a word composed of Unicode symbols.
 *
 * @private
 * @param {string} string The string to inspect.
 * @returns {boolean} Returns `true` if a word is found, else `false`.
 */
function hasUnicodeWord(string) {
  return reHasUnicodeWord.test(string);
}

/**
 * Converts `string` to an array.
 *
 * @private
 * @param {string} string The string to convert.
 * @returns {Array} Returns the converted array.
 */
function stringToArray(string) {
  return hasUnicode(string)
    ? unicodeToArray(string)
    : asciiToArray(string);
}

/**
 * Converts a Unicode `string` to an array.
 *
 * @private
 * @param {string} string The string to convert.
 * @returns {Array} Returns the converted array.
 */
function unicodeToArray(string) {
  return string.match(reUnicode) || [];
}

/**
 * Splits a Unicode `string` into an array of its words.
 *
 * @private
 * @param {string} The string to inspect.
 * @returns {Array} Returns the words of `string`.
 */
function unicodeWords(string) {
  return string.match(reUnicodeWord) || [];
}

/** Used for built-in method references. */
var objectProto = Object.prototype;

/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */
var objectToString = objectProto.toString;

/** Built-in value references. */
var Symbol = root.Symbol;

/** Used to convert symbols to primitives and strings. */
var symbolProto = Symbol ? Symbol.prototype : undefined,
    symbolToString = symbolProto ? symbolProto.toString : undefined;

/**
 * The base implementation of `_.slice` without an iteratee call guard.
 *
 * @private
 * @param {Array} array The array to slice.
 * @param {number} [start=0] The start position.
 * @param {number} [end=array.length] The end position.
 * @returns {Array} Returns the slice of `array`.
 */
function baseSlice(array, start, end) {
  var index = -1,
      length = array.length;

  if (start < 0) {
    start = -start > length ? 0 : (length + start);
  }
  end = end > length ? length : end;
  if (end < 0) {
    end += length;
  }
  length = start > end ? 0 : ((end - start) >>> 0);
  start >>>= 0;

  var result = Array(length);
  while (++index < length) {
    result[index] = array[index + start];
  }
  return result;
}

/**
 * The base implementation of `_.toString` which doesn't convert nullish
 * values to empty strings.
 *
 * @private
 * @param {*} value The value to process.
 * @returns {string} Returns the string.
 */
function baseToString(value) {
  // Exit early for strings to avoid a performance hit in some environments.
  if (typeof value == 'string') {
    return value;
  }
  if (isSymbol(value)) {
    return symbolToString ? symbolToString.call(value) : '';
  }
  var result = (value + '');
  return (result == '0' && (1 / value) == -Infinity) ? '-0' : result;
}

/**
 * Casts `array` to a slice if it's needed.
 *
 * @private
 * @param {Array} array The array to inspect.
 * @param {number} start The start position.
 * @param {number} [end=array.length] The end position.
 * @returns {Array} Returns the cast slice.
 */
function castSlice(array, start, end) {
  var length = array.length;
  end = end === undefined ? length : end;
  return (false && end >= length) ? array : baseSlice(array, start, end);
}

/**
 * Creates a function like `_.lowerFirst`.
 *
 * @private
 * @param {string} methodName The name of the `String` case method to use.
 * @returns {Function} Returns the new case function.
 */
function createCaseFirst(methodName) {
  return function(string) {
    string = toString(string);

    var strSymbols = hasUnicode(string)
      ? stringToArray(string)
      : undefined;

    var chr = strSymbols
      ? strSymbols[0]
      : string.charAt(0);

    var trailing = strSymbols
      ? castSlice(strSymbols, 1).join('')
      : string.slice(1);

    return chr[methodName]() + trailing;
  };
}

/**
 * Creates a function like `_.camelCase`.
 *
 * @private
 * @param {Function} callback The function to combine each word.
 * @returns {Function} Returns the new compounder function.
 */
function createCompounder(callback) {
  return function(string) {
    return arrayReduce(words(deburr(string).replace(reApos, '')), callback, '');
  };
}

/**
 * Checks if `value` is object-like. A value is object-like if it's not `null`
 * and has a `typeof` result of "object".
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is object-like, else `false`.
 * @example
 *
 * _.isObjectLike({});
 * // => true
 *
 * _.isObjectLike([1, 2, 3]);
 * // => true
 *
 * _.isObjectLike(_.noop);
 * // => false
 *
 * _.isObjectLike(null);
 * // => false
 */
function isObjectLike(value) {
  return !!value && typeof value == 'object';
}

/**
 * Checks if `value` is classified as a `Symbol` primitive or object.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a symbol, else `false`.
 * @example
 *
 * _.isSymbol(Symbol.iterator);
 * // => true
 *
 * _.isSymbol('abc');
 * // => false
 */
function isSymbol(value) {
  return typeof value == 'symbol' ||
    (isObjectLike(value) && objectToString.call(value) == symbolTag);
}

/**
 * Converts `value` to a string. An empty string is returned for `null`
 * and `undefined` values. The sign of `-0` is preserved.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to process.
 * @returns {string} Returns the string.
 * @example
 *
 * _.toString(null);
 * // => ''
 *
 * _.toString(-0);
 * // => '-0'
 *
 * _.toString([1, 2, 3]);
 * // => '1,2,3'
 */
function toString(value) {
  return value == null ? '' : baseToString(value);
}

/**
 * Converts `string` to [camel case](https://en.wikipedia.org/wiki/CamelCase).
 *
 * @static
 * @memberOf _
 * @since 3.0.0
 * @category String
 * @param {string} [string=''] The string to convert.
 * @returns {string} Returns the camel cased string.
 * @example
 *
 * _.camelCase('Foo Bar');
 * // => 'fooBar'
 *
 * _.camelCase('--foo-bar--');
 * // => 'fooBar'
 *
 * _.camelCase('__FOO_BAR__');
 * // => 'fooBar'
 */
var camelCase = createCompounder(function(result, word, index) {
  word = word.toLowerCase();
  return result + (index ? capitalize(word) : word);
});

/**
 * Converts the first character of `string` to upper case and the remaining
 * to lower case.
 *
 * @static
 * @memberOf _
 * @since 3.0.0
 * @category String
 * @param {string} [string=''] The string to capitalize.
 * @returns {string} Returns the capitalized string.
 * @example
 *
 * _.capitalize('FRED');
 * // => 'Fred'
 */
function capitalize(string) {
  return upperFirst(toString(string).toLowerCase());
}

/**
 * Deburrs `string` by converting
 * [Latin-1 Supplement](https://en.wikipedia.org/wiki/Latin-1_Supplement_(Unicode_block)#Character_table)
 * and [Latin Extended-A](https://en.wikipedia.org/wiki/Latin_Extended-A)
 * letters to basic Latin letters and removing
 * [combining diacritical marks](https://en.wikipedia.org/wiki/Combining_Diacritical_Marks).
 *
 * @static
 * @memberOf _
 * @since 3.0.0
 * @category String
 * @param {string} [string=''] The string to deburr.
 * @returns {string} Returns the deburred string.
 * @example
 *
 * _.deburr('déjà vu');
 * // => 'deja vu'
 */
function deburr(string) {
  string = toString(string);
  return string && string.replace(reLatin, deburrLetter).replace(reComboMark, '');
}

/**
 * Converts the first character of `string` to upper case.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category String
 * @param {string} [string=''] The string to convert.
 * @returns {string} Returns the converted string.
 * @example
 *
 * _.upperFirst('fred');
 * // => 'Fred'
 *
 * _.upperFirst('FRED');
 * // => 'FRED'
 */
var upperFirst = createCaseFirst('toUpperCase');

/**
 * Splits `string` into an array of its words.
 *
 * @static
 * @memberOf _
 * @since 3.0.0
 * @category String
 * @param {string} [string=''] The string to inspect.
 * @param {RegExp|string} [pattern] The pattern to match words.
 * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
 * @returns {Array} Returns the words of `string`.
 * @example
 *
 * _.words('fred, barney, & pebbles');
 * // => ['fred', 'barney', 'pebbles']
 *
 * _.words('fred, barney, & pebbles', /[^, ]+/g);
 * // => ['fred', 'barney', '&', 'pebbles']
 */
function words(string, pattern, guard) {
  string = toString(string);
  pattern = pattern;

  if (pattern === undefined) {
    return hasUnicodeWord(string) ? unicodeWords(string) : asciiWords(string);
  }
  return string.match(pattern) || [];
}

var lodash_camelcase = camelCase;

function upperFirstCamelcase(name) {
    const camelCased = lodash_camelcase(name);
    return `${camelCased.charAt(0).toUpperCase()}${camelCased.slice(1)}`;
}

/**
 * Logs deprecations for components, properties, methods, events, parts, and slots.
 */
class DeprecationLogger {
    /**
     * Constructs a new DeprecationLogger for a specific component.
     * @param componentName - Name of the component.
     * @param deprecationMap - Map detailing the deprecated features of the component.
     */
    constructor(componentName, deprecationMap) {
        this.componentName = componentName;
        this.deprecationMap = deprecationMap;
    }
    /**
     * Gets the configuration for a specific deprecated feature.
     * @param name - Feature name.
     * @param type - Type of deprecation (e.g., method, property).
     * @returns The deprecation details or undefined if not found.
     */
    getDeprecationConfig(name, type) {
        return this.deprecationMap[name]?.type === type ? this.deprecationMap[name] : undefined;
    }
    /**
     * Constructs a deprecation message.
     * @param args - Details to format the warning message.
     * @returns The formatted deprecation message.
     */
    createDeprecationMessage({ type, name, message }) {
        let header = '';
        if (type === 'component') {
            header = `Deprecated ${type}: "${name}"`;
        }
        else if (type === 'generic') {
            header = `Deprecation in ${this.componentName}: "${name}"`;
        }
        else {
            header = `Deprecated ${type} in ${this.componentName}: "${name}"`;
        }
        const prefix = upperFirstCamelcase(this.componentName.split('-')[0]);
        return `${header}
${message}
See the documentation for more information: https://win.gs/${prefix}WebDeprecations`;
    }
    /**
     * Logs a deprecation warning for a component feature.
     * @param args - Details to construct the warning message.
     */
    logWarning(args) {
        Logger.warn(this.createDeprecationMessage({
            ...args,
            componentName: this.componentName,
        }));
    }
    /**
     * Logs a warning if the entire component is deprecated.
     */
    warnComponent() {
        const type = 'component';
        const config = this.getDeprecationConfig(this.componentName, type);
        if (!config) {
            return;
        }
        this.logWarning({ ...config, name: this.componentName, type });
    }
    /**
     * Logs a warning for a deprecated method.
     * @param methodName - The deprecated method's name.
     * @param args - Arguments passed to the method (if any).
     */
    warnMethod(methodName, args) {
        const type = 'method';
        const config = this.getDeprecationConfig(methodName, type);
        if (!config || (config.warnCondition && !config.warnCondition(args))) {
            return;
        }
        this.logWarning({ ...config, name: formatMemberName(methodName, type) });
    }
    /**
     * Logs a warning for a deprecated property or its values.
     * @param propertyName - The deprecated property's name.
     * @param value - The property's current value (if any).
     */
    warnProperty(propertyName, value) {
        let type = 'property';
        const config = this.getDeprecationConfig(propertyName, type);
        if (!config || (config.warnCondition && !config.warnCondition(value))) {
            return;
        }
        let name = formatMemberName(propertyName, type);
        if (config.deprecatedValues) {
            if (!config.deprecatedValues.includes(value)) {
                return;
            }
            type = 'property value';
            name = String(value);
        }
        this.logWarning({
            ...config,
            name,
            type,
        });
    }
    /**
     * Checks and logs warnings for all deprecated properties in a component instance.
     * @param componentInstance - The component instance to verify.
     */
    warnProperties(componentInstance) {
        for (const name in this.deprecationMap) {
            const value = componentInstance[name];
            if (typeof value === 'undefined' || this.deprecationMap[name].type !== 'property') {
                continue;
            }
            this.warnProperty(name, value);
        }
    }
    /**
     * Logs a warning for a deprecated event.
     * @param eventName - The deprecated event's name.
     */
    warnEvent(eventName) {
        const type = 'event';
        const config = this.getDeprecationConfig(eventName, type);
        if (!config) {
            return;
        }
        this.logWarning({ ...config, name: formatMemberName(eventName, type) });
    }
    /**
     * Logs a warning for a deprecated CSS Shadow Part.
     * @param partName - The deprecated part's name.
     */
    warnPart(partName) {
        const type = 'part';
        const config = this.getDeprecationConfig(partName, type);
        if (!config) {
            return;
        }
        this.logWarning({ ...config, name: formatMemberName(partName, type) });
    }
    /**
     * Checks and logs warnings for deprecated CSS Shadow Parts in a component.
     * @param component - The component instance to verify.
     */
    warnParts(component) {
        const styleSheets = getStyleSheets();
        if (!styleSheets) {
            return;
        }
        const appliedParts = getAppliedComponentParts(component, filterStyleSheets(styleSheets));
        for (const part of appliedParts) {
            if (!(part in this.deprecationMap)) {
                continue;
            }
            this.warnPart(part);
        }
    }
    /**
     * Logs a warning for a deprecated slot.
     * @param slotName - The deprecated slot's name.
     */
    warnSlot(slotName) {
        const type = 'slot';
        const config = this.getDeprecationConfig(slotName, type);
        if (!config) {
            return;
        }
        this.logWarning({ ...config, name: formatMemberName(slotName, type) });
    }
    /**
     * Checks and logs warnings for all deprecated slots in use for a given component.
     * @param component - The component instance to verify.
     */
    warnSlots(component) {
        for (const slotName in this.deprecationMap) {
            if (this.deprecationMap[slotName].type === 'slot') {
                if (isNamedSlotUsed(component, slotName)) {
                    this.warnSlot(slotName);
                }
            }
        }
    }
    warnGeneric(item) {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => {
            const type = 'generic';
            const config = this.getDeprecationConfig(item, type);
            if (!config) {
                return;
            }
            this.logWarning({ ...config, name: formatMemberName(item, type) });
        }, 300); // 300ms delay
    }
}

const isNodeEnvironment = () => {
    return typeof process !== 'undefined' && process.versions != null && process.versions.node != null;
};

class EventEmitter {
    constructor() {
        if (!isNodeEnvironment()) {
            this.eventTarget = document.createDocumentFragment();
        }
    }
    on(eventName, callback) {
        this.eventTarget?.addEventListener(eventName, callback);
    }
    off(eventName, callback) {
        this.eventTarget?.removeEventListener(eventName, callback);
    }
    emit(eventName, payload) {
        const event = new CustomEvent(eventName, payload);
        this.eventTarget?.dispatchEvent(event);
    }
}

const interceptEvent = (event) => {
    event.preventDefault();
    event.stopPropagation();
};
/**
 * Ensure that the focus is consistently reset to the element provided.
 * Repeatedly blurring and focusing the element forces VoiceOver on iOS to
 * regain focus on the element.
 */
const forceFocus = async function (element, retries = 5) {
    element.addEventListener('focus', interceptEvent);
    element.addEventListener('blur', interceptEvent);
    for (let index = 0; index < retries; index++) {
        await nextTick();
        element.blur();
        element.focus();
    }
    element.blur();
    element.removeEventListener('focus', interceptEvent);
    element.removeEventListener('blur', interceptEvent);
    element.focus();
};

/**
 * Polyfill for the formdata event
 * https://caniuse.com/mdn-api_htmlformelement_formdata_event
 */
const formDataEventPolyfill = () => {
    if (isNodeEnvironment()) {
        return;
    }
    const windowContext = global$1.window;
    class CustomFormDataEvent extends Event {
        constructor(formData) {
            super('formdata');
            this.formData = formData;
        }
    }
    class CustomFormData extends FormData {
        constructor(form) {
            if (form) {
                super(form);
                this.form = form;
                form.dispatchEvent(new CustomFormDataEvent(this));
            }
            else {
                super();
                this.form = null;
            }
        }
        append(name, value) {
            if (!this.form) {
                super.append(name, value);
                return;
            }
            let formElement = this.getFormElement(name);
            if (!formElement) {
                const input = this.createHiddenInput(name);
                this.form.appendChild(input);
                formElement = input;
            }
            if (this.has(name)) {
                this.handleMultipleEntries(name, value, formElement);
            }
            else {
                super.append(name, value);
            }
            formElement.value = value;
        }
        getFormElement(name) {
            return this.form ? this.form.elements[name] : null;
        }
        createHiddenInput(name) {
            const input = windowContext.document.createElement('input');
            input.type = 'hidden';
            input.name = name;
            return input;
        }
        handleMultipleEntries(name, value, formElement) {
            const entries = this.getAll(name);
            const index = entries.indexOf(formElement.value);
            if (index !== -1) {
                entries.splice(index, 1);
            }
            entries.push(value);
            this.set(name, entries.toString());
        }
    }
    const isFormDataEventSupported = () => {
        return 'FormDataEvent' in windowContext;
    };
    const applyFormDataPolyfill = () => {
        if (!windowContext.FormData || isFormDataEventSupported()) {
            return;
        }
        windowContext.FormData = CustomFormData;
        windowContext.addEventListener('submit', (event) => {
            if (!event.defaultPrevented) {
                new FormData(event.target);
            }
        });
    };
    if (windowContext.document.readyState === 'complete') {
        applyFormDataPolyfill();
    }
    else {
        windowContext.addEventListener('DOMContentLoaded', applyFormDataPolyfill);
    }
};

const hasReducedMotion = () => {
    return global$1.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

const isTouchDevice = () => {
    return 'ontouchstart' in global$1.window || global$1.navigator.maxTouchPoints > 0;
};

const requestFormSubmit = (form) => {
    if (!form) {
        return;
    }
    if (typeof HTMLFormElement.prototype.requestSubmit === 'function') {
        return form.requestSubmit();
    }
    // Fallback for Safari
    if (!isNodeEnvironment()) {
        const ghostButton = global$1.document.createElement('button');
        ghostButton.type = 'submit';
        ghostButton.style.display = 'none';
        form.appendChild(ghostButton);
        ghostButton.click();
        ghostButton.remove();
    }
};

const responsiveStringRegex = /([a-zA-Z0-9-]+)@([a-zA-Z0-9-]+)/;
const isResponsiveProperty = (value) => {
    return typeof value === 'string' && responsiveStringRegex.test(value);
};
const parseResponsiveProperty = (responsiveProperty) => {
    const properties = responsiveProperty.match(new RegExp(responsiveStringRegex, 'g')) || [];
    return properties.reduce((acc, property) => {
        const match = property.match(responsiveStringRegex);
        if (match) {
            const [, value, breakpoint] = match;
            acc[breakpoint] = value;
        }
        return acc;
    }, {});
};
const serializeResponsiveProperty = (responsiveProperty) => {
    return Object.entries(responsiveProperty)
        .filter(([, value]) => value != null)
        .map(([breakpoint, value]) => `${value}@${breakpoint}`)
        .join(' ');
};
const mapResponsiveProperty = (input, dictionary) => {
    return Object.entries(input).reduce((acc, [breakpoint, value]) => {
        if (value && dictionary[value]) {
            acc[breakpoint] = dictionary[value];
        }
        return acc;
    }, {});
};
/**
 * Checks if a given host element has a named slot.
 *
 * @param host - The host element to check.
 * @param slotName - The name of the slot to check for.
 * @returns `true` if the named slot exists, `false` otherwise.
 */
function hasNamedSlot(host, slotName) {
    if (!host || typeof host.querySelector !== 'function') {
        return false;
    }
    try {
        return host.querySelector(`:scope > [slot="${slotName}"]`) !== null;
    }
    catch (e) {
        return host.querySelector(`[slot="${slotName}"]`) !== null;
    }
}
/**
 * Retrieves the concatenated text content of all nodes in a default slot.
 *
 * @param slot - The slot element to extract text from.
 * @returns The concatenated text content of the slot's nodes, trimmed.
 */
function getDefaultSlotTextContent(slot) {
    if (!slot || typeof slot.assignedNodes !== 'function') {
        return '';
    }
    return slot.assignedNodes({ flatten: true }).reduce((acc, node) => acc + (node.textContent || ''), '').trim();
}
/**
 * Retrieves the text content of a named slot within a host element.
 *
 * @param host - The host element containing the named slot.
 * @param slotName - The name of the slot.
 * @returns The trimmed text content of the named slot, or an empty string if not found.
 */
function getNamedSlotTextContent(host, slotName) {
    if (!host || typeof host.querySelector !== 'function') {
        return '';
    }
    try {
        return host.querySelector(`:scope > [slot="${slotName}"]`)?.textContent?.trim() || '';
    }
    catch (e) {
        return host.querySelector(`[slot="${slotName}"]`)?.textContent?.trim() || '';
    }
}

/**
 * SlotMutationService observes and handles mutations in slotted content using MutationObserver.
 * It's particularly useful in environments where the 'slotchange' event is unreliable.
 *
 * @remarks
 * This service addresses specific issues with slot change detection:
 * - In Angular, unnamed slot changes are not detected via 'slotchange' events when using string interpolation with deferred slotted content.
 * - The 'slotchange' events are not triggered on initial load for prerendered content.
 *
 * @class
 */
class SlotMutationService {
    constructor() {
        this.observers = new Map();
        this.isNodeEnv = isNodeEnvironment();
        this.observer = this.isNodeEnv ? null : this.createObserver();
    }
    /**
     * Gets the singleton instance of SlotMutationService.
     *
     * @returns {SlotMutationService} The singleton instance of SlotMutationService.
     * @static
     */
    static getInstance() {
        if (!SlotMutationService.instance) {
            SlotMutationService.instance = new SlotMutationService();
        }
        return SlotMutationService.instance;
    }
    /**
     * Starts observing the given element for mutations.
     *
     * @remarks
     * It is recommended to call this method in the `componentWillLoad` lifecycle method
     * of a Stencil component to ensure that the element is observed as soon as it is loaded.
     *
     * @param {HTMLElement} element - The element to observe.
     * @param {() => void} handleSlotChange - The callback to execute when a mutation is observed.
     * @returns {void}
     */
    observe(element, handleSlotChange) {
        if (this.isNodeEnv || this.observers.has(element))
            return;
        this.observers.set(element, handleSlotChange);
        this.observer?.observe(element, SlotMutationService.OBSERVER_OPTIONS);
        handleSlotChange.call(element);
    }
    /**
     * Stops observing the given element for mutations.
     *
     * @remarks
     * It is recommended to call this method in the `disconnectedCallback` lifecycle method
     * of a Stencil component to ensure that the element is no longer observed when it is removed from the DOM.
     *
     * @param {HTMLElement} element - The element to stop observing.
     * @returns {void}
     */
    unobserve(element) {
        if (this.isNodeEnv || !this.observers.has(element))
            return;
        this.observers.delete(element);
        if (this.observers.size === 0) {
            this.observer?.disconnect();
        }
    }
    /**
     * Creates a MutationObserver instance.
     *
     * @returns {MutationObserver} The created MutationObserver instance.
     * @private
     */
    createObserver() {
        return new MutationObserver(this.handleMutations.bind(this));
    }
    /**
     * Handles observed mutations.
     *
     * @param {MutationRecord[]} mutations - The array of observed mutations.
     * @returns {void}
     * @private
     */
    handleMutations(mutations) {
        mutations.forEach((mutation) => {
            this.observers.forEach((handleSlotChange, element) => {
                if (element.contains(mutation.target)) {
                    handleSlotChange.call(element);
                }
            });
        });
    }
}
SlotMutationService.instance = null;
SlotMutationService.OBSERVER_OPTIONS = {
    childList: true,
    subtree: true,
    characterData: true,
};
/**
 * Singleton instance of SlotMutationService.
 */
const slotMutationService = SlotMutationService.getInstance();

export { CSSTransition as C, DeprecationLogger as D, EventEmitter as E, forceFocus as a, bem as b, isTouchDevice as c, isResponsiveProperty as d, serializeResponsiveProperty as e, formDataEventPolyfill as f, global$1 as g, hasNamedSlot as h, isNodeEnvironment as i, getDefaultSlotTextContent as j, getNamedSlotTextContent as k, hasReducedMotion as l, mapResponsiveProperty as m, nextTick as n, parseResponsiveProperty as p, requestFormSubmit as r, slotMutationService as s };
